import SearchIcon from "@mui/icons-material/Search";
import { Box, CircularProgress, InputAdornment, List, ListItemButton, ListItemText, TextField, Typography } from "@mui/material";
import { FC, useEffect, useState } from "react";

import { Dialog } from "src/components/Dialog";
import { Firm, useLazyFirmListQuery } from "src/services/firm";

interface Props {
    open: boolean;
    onClose: () => void;
    onSelect: (firm: Firm) => void;
    pickedIds?: number[];
    title?: string;
}

export const FirmPickerModal: FC<Props> = ({ open, onClose, onSelect, pickedIds, title = 'Add firm' }) => {
    const [search, setSearch] = useState("");
    const [fetchFirms, { data, isFetching }] = useLazyFirmListQuery();
    const [alreadyPickedAlertOpen, setAlreadyPickedAlertOpen] = useState(false);

    useEffect(() => {
        if (open) {
            fetchFirms({ limit: 50 });
        }
    }, [open]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            fetchFirms({ search: search || undefined, limit: 50 });
        }, 300);
        return () => clearTimeout(timeout);
    }, [search]);

    const displayAlreadyPickedAlert = () => {
        setAlreadyPickedAlertOpen(true);
        setTimeout(() => {
            setAlreadyPickedAlertOpen(false);
        }, 3000);
    };

    const handleSelect = (firm: Firm) => {
        if (pickedIds?.includes(firm.id)) {
            displayAlreadyPickedAlert();
            return;
        }
        onSelect(firm);
        onClose();
    };

    return (
        <Dialog
            isOpen={open}
            onClose={onClose}
            title={title}
            sx={{ width: 560, height: 520, padding: "24px" }}
        >
            <TextField
                fullWidth
                placeholder="Search firms..."
                size="small"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                    ),
                }}
                sx={{ mb: 1.5 }}
            />
            <Box sx={{ overflowY: "auto", flex: 1, height: 360 }}>
                {isFetching ? (
                    <Box sx={{ display: "flex", justifyContent: "center", pt: 4 }}>
                        <CircularProgress size={28} />
                    </Box>
                ) : data?.items.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", pt: 4 }}>
                        No firms found
                    </Typography>
                ) : (
                    <List disablePadding>
                        {data?.items.map((firm) => (
                            <ListItemButton
                                key={firm.id}
                                onClick={() => handleSelect(firm)}
                                disabled={pickedIds?.includes(firm.id)}
                                sx={{ borderRadius: 1, px: 1, py: 0.75 }}
                            >
                                <ListItemText
                                    primary={firm.name}
                                    primaryTypographyProps={{ variant: "body2", fontWeight: 500, noWrap: true }}
                                />
                            </ListItemButton>
                        ))}
                    </List>
                )}
                {alreadyPickedAlertOpen && (
                    <Box sx={{ position: "absolute", bottom: 16, left: "50%", transform: "translateX(-50%)", bgcolor: "error.main", color: "#fff", px: 2, py: 1, borderRadius: 1 }}>
                        <Typography variant="body2">This firm is already added</Typography>
                    </Box>
                )}
            </Box>
        </Dialog>
    );
};
